export const DEFAULT_TIERS = {
  bronce: { min: 0, nombre: 'Bronce', multiplicador: 1 },
  plata: { min: 1000, nombre: 'Plata', multiplicador: 1.2 },
  oro: { min: 3000, nombre: 'Oro', multiplicador: 1.5 },
};

// 1 punto cada $10. La caja usa el valor guardado en Configuración.
export const DEFAULT_PUNTOS_POR_PESO = 0.1;

export function getPuntosPorPeso(config) {
  const value = Number(config?.puntosPorPeso);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_PUNTOS_POR_PESO;
}

export function getPesosPorPunto(config) {
  const rate = getPuntosPorPeso(config);
  const pesos = 1 / rate;
  return Number.isInteger(pesos) ? pesos : Number(pesos.toFixed(2));
}

export function getTiers(config) {
  const incoming = config?.tiers || {};
  return {
    bronce: { ...DEFAULT_TIERS.bronce, ...(incoming.bronce || {}) },
    plata: { ...DEFAULT_TIERS.plata, ...(incoming.plata || {}) },
    oro: { ...DEFAULT_TIERS.oro, ...(incoming.oro || {}) },
  };
}

export function getTierForPoints(points, config) {
  const tiers = getTiers(config);
  const total = Number(points) || 0;
  let current = { key: 'bronce', ...tiers.bronce, multiplicador: Number(tiers.bronce.multiplicador) || 1 };

  Object.entries(tiers)
    .sort((a, b) => (Number(a[1].min) || 0) - (Number(b[1].min) || 0))
    .forEach(([key, data]) => {
      if (total >= (Number(data.min) || 0)) {
        current = {
          key,
          nombre: data.nombre || key,
          min: Number(data.min) || 0,
          multiplicador: Number(data.multiplicador) || 1,
        };
      }
    });

  return current;
}

export function calculatePurchasePoints(amount, config, multiplier = 1) {
  const pesos = parseFloat(amount);
  if (!Number.isFinite(pesos) || pesos <= 0) return 0;
  const rate = getPuntosPorPeso(config);
  const mult = Number(multiplier) || 1;
  return Math.round(pesos * rate * mult);
}

export function conversionExample(config, amount = 10000) {
  const tiers = getTiers(config);
  return Object.entries(tiers).map(([key, data]) => ({
    key,
    nombre: data.nombre,
    puntos: calculatePurchasePoints(amount, config, data.multiplicador),
    multiplicador: Number(data.multiplicador) || 1,
  }));
}

export function money(value) {
  return Number(value || 0).toLocaleString('es-AR');
}

export function getRewardTerms(reward = {}) {
  const puntos = Number(reward.puntosRequeridos || reward.pointsCost || 0);
  const costo = Number(reward.costo || 0);
  const precioVenta = Number(reward.precioVenta || 0);
  const hasPlata = reward.plata != null && reward.plata !== '';
  const plata = Number(hasPlata ? reward.plata : costo);
  return { puntos, plata, costo, precioVenta };
}

export function formatRewardDeal(reward) {
  const { puntos, plata } = getRewardTerms(reward);
  if (plata > 0) return `${money(puntos)} pts + $${money(plata)}`;
  return `${money(puntos)} pts`;
}

export function suggestedRewardPoints(reward, config) {
  const { plata, precioVenta } = getRewardTerms(reward);
  const descuento = precioVenta - plata;
  if (descuento <= 0) return 0;
  return Math.max(1, Math.round(descuento * getPuntosPorPeso(config)));
}

export function validateRewardEconomics(reward) {
  const { puntos, plata, costo, precioVenta } = getRewardTerms(reward);
  if (puntos <= 0) return 'El premio tiene que pedir puntos.';
  if (plata < costo) {
    return `El aporte en dinero ($${money(plata)}) no cubre el costo ($${money(costo)}).`;
  }
  if (precioVenta > 0 && plata > precioVenta) {
    return `El aporte en dinero no puede ser mayor al precio normal ($${money(precioVenta)}).`;
  }
  return '';
}

export function analyzeRewardEconomics(reward) {
  const { puntos, plata, costo, precioVenta } = getRewardTerms(reward);
  const margenNormal = precioVenta - costo;
  const margenPct = precioVenta > 0 ? (margenNormal / precioVenta) * 100 : 0;
  return {
    puntos,
    plata,
    costo,
    precioVenta,
    margenNormal,
    margenPct,
    aporteCliente: plata,
    costoCubierto: plata >= costo,
    valorCedido: Math.max(0, precioVenta - plata),
    diferenciaVenta: precioVenta - plata,
  };
}

/** Simulación de puntos. NO usar en caja productiva. */
export function simulateFloorPoints(amount, pesosPorPunto) {
  const compra = Number(amount);
  const tasa = Number(pesosPorPunto);
  if (!(compra > 0) || !(tasa > 0)) return 0;
  return Math.floor(compra / tasa);
}

export function isRewardLive(reward, now = new Date()) {
  if (!reward || reward.activo === false) return false;
  const start = toJsDate(reward.fechaInicio);
  const end = toJsDate(reward.fechaFin);
  if (start && start > now) return false;
  if (end && end < now) return false;
  const stock = Number(reward.stock ?? -1);
  if (stock === 0) return false;
  return true;
}

export function sortCatalog(rewards) {
  return [...rewards].sort((a, b) => {
    const dest = (b.destacado ? 1 : 0) - (a.destacado ? 1 : 0);
    if (dest !== 0) return dest;
    return (Number(a.orden) || 999) - (Number(b.orden) || 999);
  });
}

export function usableRewards(rewards, userPoints) {
  return sortCatalog(rewards.filter((r) => isRewardLive(r) && userPoints >= getRewardTerms(r).puntos));
}

export function nearbyRewards(rewards, userPoints, limit = 3) {
  return rewards
    .filter((r) => isRewardLive(r) && getRewardTerms(r).puntos > userPoints)
    .map((reward) => ({
      reward,
      faltan: getRewardTerms(reward).puntos - userPoints,
    }))
    .sort((a, b) => a.faltan - b.faltan)
    .slice(0, limit);
}

export function toJsDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toDateInput(value) {
  const date = toJsDate(value);
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDateAr(value) {
  const date = toJsDate(value);
  if (!date) return '';
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function getVoucherStatus(voucher, now = new Date()) {
  const raw = String(voucher?.estado || '').toLowerCase();
  if (raw === 'utilizado' || raw === 'canjeado') return 'UTILIZADO';
  if (raw === 'cancelado') return 'CANCELADO';
  if (raw === 'vencido') return 'VENCIDO';
  const exp = toJsDate(voucher?.fechaVencimiento || voucher?.expiracion);
  if (exp && exp < now) return 'VENCIDO';
  return 'ACTIVO';
}

export function getVoucherName(voucher) {
  return voucher?.premioNombre || voucher?.beneficioNombre || 'Beneficio';
}

export function getVoucherPoints(voucher) {
  return Number(voucher?.puntosUtilizados ?? voucher?.puntosUsados ?? voucher?.puntos ?? 0);
}

export function getVoucherCash(voucher) {
  return Number(voucher?.aporteDinero ?? voucher?.plata ?? 0);
}

export function movementKind(mov) {
  const tipo = String(mov?.tipo || '').toUpperCase();
  const desc = String(mov?.descripcion || '').toLowerCase();
  if (tipo === 'CANJE') return 'canje';
  if (tipo === 'BIENVENIDA' || desc.includes('bienvenida')) return 'bienvenida';
  if (tipo === 'AJUSTE' || tipo === 'DEBITO' || desc.includes('ajuste')) return 'ajuste';
  if (tipo === 'CREDITO') return 'compra';
  return 'movimiento';
}

export function movementTitle(mov) {
  const kind = movementKind(mov);
  if (kind === 'canje') {
    const name = mov.premioNombre || mov.beneficioNombre || '';
    return name ? `Canje ${name}` : 'Canje';
  }
  if (kind === 'bienvenida') return 'Bienvenida';
  if (kind === 'ajuste') return 'Ajuste';
  if (kind === 'compra') return 'Compra';
  return mov?.descripcion || 'Movimiento';
}

export function movementDelta(mov) {
  const kind = movementKind(mov);
  const pts = Number(mov?.puntos || 0);
  if (kind === 'canje') return -Math.abs(pts);
  if (kind === 'ajuste') return pts;
  return Math.abs(pts);
}

export function withRunningBalance(movimientos, currentBalance) {
  const sorted = [...movimientos].sort((a, b) => {
    const da = toJsDate(a.fecha)?.getTime() || 0;
    const db = toJsDate(b.fecha)?.getTime() || 0;
    return da - db;
  });
  let cursor = Number(currentBalance || 0);
  const newestFirst = [...sorted].reverse();
  return newestFirst.map((mov) => {
    const saldoPosterior = mov.saldoPosterior != null ? Number(mov.saldoPosterior) : cursor;
    const delta = movementDelta(mov);
    cursor = saldoPosterior - delta;
    return { ...mov, saldoMostrado: saldoPosterior, delta };
  });
}
